/**
 * AssistantMessage — renderer chat odpovědi s custom action bloky.
 *
 * Filip 2026-07-08: extract z superagent modulu (2117 řádků těžkopádnosti)
 * jen to co Codable chat potřebuje — planning + patch preview + commit,
 * bez agent runtime, bez session storage. Čistě renderer + callback.
 *
 * Speciální ```language bloky v LLM response:
 *  - ```plan       → PLÁN karta (Accept/Reject/Edit)
 *  - ```patch soubor.tsx → DIFF preview s Apply buttonem
 *  - ```commit "msg"    → COMMIT + PUSH action
 *  - ```question   → LLM se ptá — Reply input
 *  - ```tool_read cesta → kompaktní řádek tool aktivity (spinner → check)
 *
 * LLM je vedený system promptem (v CodableChatPane systemPreamble).
 * Akce zatím jen `onAction` callback — implementace commit/patch přijde
 * v následujícím MR (backend tool calls do repa).
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Edit3,
  GitCommit,
  HelpCircle,
  Loader2,
  Undo2,
  Wrench,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { Button } from '../../../components/ui';
import {
  blockAutoClaimKey,
  chatAutoCardUi,
  planAutoClaimKey,
} from '../../../lib/codable-chat-auto';

export type ActionType = 'plan_accept' | 'plan_reject' | 'plan_edit'
  | 'patch_apply' | 'patch_reject' | 'patch_revert' | 'patch_error'
  | 'commit_confirm' | 'commit_reject'
  | 'question_reply';

export interface ActionPayload {
  type: ActionType;
  block: string; // původní obsah bloku (plan text, patch, commit msg)
  meta?: Record<string, string>; // path pro patch, msg pro commit, atd.
}

interface Props {
  content: string;
  onAction?: (payload: ActionPayload) => void;
  /** Slug Codable projektu — pro real backend akce (patch apply → chat_file_write) */
  projectSlug?: string;
  /** Zpráva ještě streamuje / tool cally běží → chips ukazují spinner místo check */
  pending?: boolean;
}

// Sdílené prose třídy — gray-* tokeny (invertují se s data-theme day/night).
// prose-invert drží dark-UI default; light themes přemapuje index.css.
// Explicitní prose-*:text-gray-* = pojistka proti nečitelným pastelům.
export const PROSE_CLASSES = 'prose prose-invert prose-sm max-w-none text-[13px] leading-relaxed text-gray-200 '
  + 'prose-p:my-1 prose-p:text-[13px] prose-p:text-gray-200 prose-p:leading-relaxed prose-p:break-words '
  + 'prose-headings:my-1 prose-headings:font-semibold prose-headings:text-gray-100 '
  + 'prose-h1:text-sm prose-h2:text-[13px] prose-h3:text-[13px] '
  + 'prose-h1:leading-snug prose-h2:leading-snug prose-h3:leading-snug '
  + 'prose-strong:text-gray-100 prose-a:text-blue-400 '
  + 'prose-pre:bg-gray-900 prose-pre:text-gray-200 prose-pre:text-[11px] prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap '
  + 'prose-code:text-blue-300 prose-code:text-[12px] prose-code:break-all '
  + 'prose-li:my-0.5 prose-li:text-[13px] prose-li:text-gray-200 prose-ul:my-1 prose-ol:my-1 '
  + '[&_*]:max-w-full [&_*]:break-words';

// Regex parse: ```lang optional_meta\n...content...``` or inline ```step_done 2```.
// Nezachytí markdown inline v obsahu (rendered as-is uvnitř karty)
const BLOCK_RE =
  /```(plan|patch|commit|question|tool_read|step_done)(?:[ \t]+([^\n`]*?))?(?:\n([\s\S]*?)\n?)?```/g;

interface Segment {
  type: 'markdown' | 'action';
  content: string;
  action?: 'plan' | 'patch' | 'commit' | 'question' | 'tool_read' | 'step_done';
  meta?: string;
}

function parseSegments(text: string): Segment[] {
  const out: Segment[] = [];
  let lastEnd = 0;
  const re = new RegExp(BLOCK_RE);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastEnd) {
      out.push({ type: 'markdown', content: text.slice(lastEnd, m.index) });
    }
    out.push({
      type: 'action',
      action: m[1] as Segment['action'],
      meta: m[2],
      content: m[3] ?? '',
    });
    lastEnd = m.index + m[0].length;
  }
  if (lastEnd < text.length) {
    out.push({ type: 'markdown', content: text.slice(lastEnd) });
  }
  if (out.length === 0) out.push({ type: 'markdown', content: text });
  return out;
}

const mdComponents: Components = {
  code({ className, children, ...props }) {
    // Fallback pro nespeciální code fences — standard render
    return <code className={className} {...props}>{children}</code>;
  },
};

const RAW_CODE_START_RE =
  /^[ \t]*(<!doctype\s+html\b|<html\b|<script\b|<style\b|import\s+[\s\S]*?\s+from\s+['"]|export\s+(?:default\s+)?|function\s+\w+\s*\(|const\s+\w+\s*=|let\s+\w+\s*=|var\s+\w+\s*=|interface\s+\w+\b|type\s+\w+\s*=|class\s+\w+\b|{\s*["\w-]+:)/im;

function looksLikeRawCodeDump(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;

  if (/^(<!doctype\s+html\b|<html\b|<script\b|<style\b)/i.test(trimmed)) return true;
  if (/^({|\[)\s*[\r\n]/.test(trimmed) && trimmed.length > 400) return true;

  const lines = trimmed.split(/\r?\n/);
  if (lines.length < 6 && trimmed.length < 800) return false;

  let score = 0;
  if (/^import\s+[\s\S]*?\s+from\s+['"]/m.test(trimmed)) score += 1;
  if (/^export\s+(default\s+)?/m.test(trimmed)) score += 1;
  if (/\b(function|const|let|var)\s+[A-Za-z_$][\w$]*/.test(trimmed)) score += 1;
  if (/<[A-Za-z][^>]*>/.test(trimmed) || /<\/[A-Za-z][^>]*>/.test(trimmed)) score += 1;
  if (/\b(className|onClick|useState|useEffect)\b/.test(trimmed)) score += 1;
  if (/\breturn\s*\(|=>\s*[{(]/.test(trimmed)) score += 1;

  return score >= 2;
}

function rawCodeStartIndex(text: string): number {
  const re = new RegExp(RAW_CODE_START_RE);
  const m = re.exec(text);
  if (!m) return -1;
  const candidate = text.slice(m.index);
  return looksLikeRawCodeDump(candidate) ? m.index : -1;
}

function codeDumpLabel(text: string): string {
  const trimmed = text.trim();
  if (/^(<!doctype\s+html\b|<html\b)/i.test(trimmed)) return 'HTML';
  if (/\b(className|useState|useEffect)\b/.test(trimmed)) return 'TSX';
  if (/^(import|export|const|let|var|function|type|interface|class)\b/m.test(trimmed)) return 'Code';
  if (/^({|\[)\s*[\r\n]/.test(trimmed)) return 'JSON';
  return 'Text';
}

function CodeDumpBlock({ content }: { content: string }) {
  const { t } = useTranslation();
  const trimmed = content.trim();
  return (
    <div className='rounded-lg border border-gray-800 bg-gray-950 overflow-hidden'>
      <div className='flex items-center justify-between gap-3 border-b border-gray-800 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wide text-gray-500'>
        <span>{t('codable.asRawDump', 'Nezaobalený výpis ({{kind}})', { kind: codeDumpLabel(trimmed) })}</span>
        <span>{t('codable.asCharCount', '{{count}} znaků', { count: trimmed.length.toLocaleString() })}</span>
      </div>
      <pre className='max-h-[360px] overflow-auto p-2.5 text-[11px] leading-relaxed text-gray-300 whitespace-pre font-mono'>
        {trimmed}
      </pre>
    </div>
  );
}

function MarkdownOrCodeDump({ content }: { content: string }) {
  const start = rawCodeStartIndex(content);
  if (start === -1) {
    return (
      <div className={PROSE_CLASSES}>
        <ReactMarkdown components={mdComponents}>{content}</ReactMarkdown>
      </div>
    );
  }

  const before = content.slice(0, start).trim();
  const dump = content.slice(start).trim();
  return (
    <div className='space-y-2'>
      {before ? (
        <div className={PROSE_CLASSES}>
          <ReactMarkdown components={mdComponents}>{before}</ReactMarkdown>
        </div>
      ) : null}
      <CodeDumpBlock content={dump} />
    </div>
  );
}

// Render položky — tool_read bloky se slučují do skupin (kompaktní řádky)
type RenderItem =
  | { kind: 'segment'; seg: Segment }
  | { kind: 'tools'; paths: string[] };

function groupSegments(segments: Segment[]): RenderItem[] {
  const items: RenderItem[] = [];
  for (const s of segments) {
    if (s.type === 'action' && s.action === 'tool_read') {
      const path = (s.meta ?? '').trim();
      const last = items[items.length - 1];
      if (last?.kind === 'tools') last.paths.push(path);
      else items.push({ kind: 'tools', paths: [path] });
      continue;
    }
    // Whitespace-only mezery mezi bloky nerenderuj (jinak by rozbily skupiny)
    if (s.type === 'markdown' && !s.content.trim()) continue;
    items.push({ kind: 'segment', seg: s });
  }
  return items;
}

export function AssistantMessage({
  content, onAction, projectSlug, pending, autoAccept, autoCommit, claimAuto, wasAuto, planDecision }: Props & {
    /** Auto-accept rezim: plan karty se prijmou a patch karty aplikuji samy. */
    autoAccept?: boolean;
    /** Live+Commit: commit karty se pushnou samy (stejný claim guard). */
    autoCommit?: boolean;
    /** Claim guard — vrati true jen prvnimu volajicimu pro dany klic. */
    claimAuto?: (key: string) => boolean;
    /** Peek — karta už byla auto-vyřízená (reload / StrictMode remount). */
    wasAuto?: (key: string) => boolean;
    /** Parent už plán vyřídil (stream-end Auto/Live) — karta jen badge. */
    planDecision?: 'auto' | 'accepted' | 'rejected';
  }) {
  const { t } = useTranslation();
  const items = groupSegments(parseSegments(content));
  return (
    <div className='space-y-3'>
      {items.map((item, i) => {
        if (item.kind === 'tools') {
          return <ToolReadRows key={i} paths={item.paths} pending={pending} />;
        }
        const s = item.seg;
        if (s.type === 'markdown') {
          return <MarkdownOrCodeDump key={i} content={s.content} />;
        }
        if (s.action === 'plan') {
          return (
            <PlanCard
              key={i}
              content={s.content}
              onAction={onAction}
              autoAccept={autoAccept}
              pending={pending}
              autoKey={planAutoClaimKey(s.content)}
              claimAuto={claimAuto}
              wasAuto={wasAuto}
              initialDecision={planDecision}
            />
          );
        }
        if (s.action === 'patch') {
          return (
            <PatchCard
              key={i}
              autoApply={autoAccept}
              pending={pending}
              autoKey={blockAutoClaimKey('patch', s.content, i)}
              claimAuto={claimAuto}
              wasAuto={wasAuto}
              content={s.content}
              path={s.meta ?? ''}
              projectSlug={projectSlug}
              onAction={onAction}
            />
          );
        }
        if (s.action === 'commit') {
          return (
            <CommitCard
              key={i}
              msg={s.meta ?? ''}
              body={s.content}
              projectSlug={projectSlug}
              onAction={onAction}
              autoCommit={autoCommit}
              pending={pending}
              autoKey={blockAutoClaimKey('commit', `${s.meta ?? ''}\n${s.content}`, i)}
              claimAuto={claimAuto}
              wasAuto={wasAuto}
            />
          );
        }
        if (s.action === 'question') {
          return <QuestionCard key={i} content={s.content} onAction={onAction} />;
        }
        if (s.action === 'step_done') {
          // Kompaktní chip „✓ krok N hotov" — activePlan v CodableChatPane už byl updatnut
          // ve fence parseru; tady jen vizuální markera. `meta` = N (1-based).
          const n = (s.meta ?? '').trim();
          return (
            <div key={i} className='inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-mono'>
              <CheckCircle2 size={11} />
              {t('codable.asStepDone', 'Krok {{n}} hotov', { n })}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

/** Stavové barvy pro tool chips: pending amber → done emerald (error red řeší karty) */
function toolChipClass(pending?: boolean) {
  return pending
    ? 'bg-amber-500/10 text-amber-300 border-amber-500/20'
    : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20';
}

function ToolReadRows({ paths, pending }: { paths: string[]; pending?: boolean }) {
  const { t } = useTranslation();
  const chip = (p: string, i: number) => (
    <span
      key={i}
      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono ${toolChipClass(pending)}`}
      title={p}
    >
      <Wrench size={10} className='shrink-0' />
      <span className='truncate max-w-[260px]'>
        {t('codable.asReading', 'Čtu {{path}}', { path: p || t('codable.asFileFallback', 'soubor') })}
      </span>
      {pending
        ? <Loader2 size={10} className='animate-spin shrink-0' />
        : <Check size={10} className='shrink-0' />}
    </span>
  );
  // Víc než 3 kroky → sbalitelná <details> skupina
  if (paths.length > 3) {
    return (
      <details className='group'>
        <summary
          className={`cursor-pointer list-none inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono select-none ${toolChipClass(pending)}`}
        >
          <ChevronRight size={10} className='shrink-0 transition-transform group-open:rotate-90' />
          {t('codable.asStepCount', '{{count}} kroků', { count: paths.length })}
          {pending
            ? <Loader2 size={10} className='animate-spin shrink-0' />
            : <Check size={10} className='shrink-0' />}
        </summary>
        <div className='mt-1.5 flex flex-col items-start gap-1 pl-3'>
          {paths.map(chip)}
        </div>
      </details>
    );
  }
  return <div className='flex flex-col items-start gap-1'>{paths.map(chip)}</div>;
}

function PlanCard({
  content, onAction, autoAccept, pending, autoKey, claimAuto, wasAuto, initialDecision }: {
    content: string;
    onAction?: (p: ActionPayload) => void;
    autoAccept?: boolean;
    pending?: boolean;
    autoKey?: string;
    claimAuto?: (key: string) => boolean;
    wasAuto?: (key: string) => boolean;
    initialDecision?: 'auto' | 'accepted' | 'rejected';
  }) {
  const { t } = useTranslation();
  const claimed = !!(autoKey && wasAuto?.(autoKey));
  // Karta ma STAV — po rozhodnuti ukaze badge, ne mrtva tlacitka
  // (Filip 2026-07-18: "tlacitko jakoby nic nedela.. je to matouci").
  const [decision, setDecision] = useState<'idle' | 'accepted' | 'rejected' | 'auto'>(
    initialDecision ?? (claimed ? 'auto' : 'idle'),
  );
  useEffect(() => {
    if (initialDecision && decision === 'idle') setDecision(initialDecision);
  }, [initialDecision, decision]);
  useEffect(() => {
    // Parent (stream-end) už přijal — nevolej znovu.
    if (initialDecision) return;
    if (!autoAccept || pending || decision !== 'idle') return;
    // Claim selhal = stream-end / dřívější auto už proběhlo → jen badge.
    if (autoKey && claimAuto && !claimAuto(autoKey)) {
      setDecision('auto');
      return;
    }
    setDecision('auto');
    onAction?.({ type: 'plan_accept', block: content, meta: { auto: 'true' } });
    // content záměrně mimo deps — claimAuto drží one-shot; pending false spouští accept
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAccept, pending, initialDecision]);
  return (
    <div className='border border-blue-500/30 bg-blue-500/5 rounded-xl p-3'>
      <div className='flex items-center gap-2 mb-2'>
        <Edit3 size={13} className='text-blue-400' />
        <span className='text-[11px] uppercase font-bold text-blue-400 tracking-wider'>
          {t('codable.asPlanCard', 'Návrh plánu')}
        </span>
      </div>
      <div className={`${PROSE_CLASSES} mb-3`}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
      {decision !== 'idle' && (
        <div
          className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-semibold ${
            decision === 'rejected'
              ? 'bg-gray-500/10 text-gray-400'
              : 'bg-emerald-500/10 text-emerald-400'
          }`}
        >
          <CheckCircle2 size={13} />
          {decision === 'accepted'
            ? t('codable.planAccepted', 'Plán přijat — agent pokračuje')
            : decision === 'auto'
            ? t('codable.planHelperBadge', 'Checklist (pomocný — ne brána schválení)')
            : t('codable.planRejectedBadge', 'Plán odmítnut')}
        </div>
      )}
      {decision === 'idle' && (
      <div className='flex gap-2'>
        <Button
          size='sm'
          variant='success'
          icon={<CheckCircle2 size={14} />}
          onClick={() => {
            setDecision('accepted');
            onAction?.({ type: 'plan_accept', block: content });
          }}
        >
          {t('codable.chatAccept')}
        </Button>
        <Button
          size='sm'
          variant='ghost'
          icon={<Edit3 size={14} />}
          onClick={() => onAction?.({ type: 'plan_edit', block: content })}
        >
          {t('common.edit', 'Upravit')}
        </Button>
        <Button
          size='sm'
          variant='danger'
          icon={<XCircle size={14} />}
          onClick={() => {
            setDecision('rejected');
            onAction?.({ type: 'plan_reject', block: content });
          }}
        >
          {t('codable.chatRemove')}
        </Button>
      </div>
      )}
    </div>
  );
}

function PatchCard({
  content,
  path,
  projectSlug,
  onAction,
  autoApply,
  pending,
  autoKey,
  claimAuto,
  wasAuto,
}: {
  content: string;
  path: string;
  projectSlug?: string;
  onAction?: (p: ActionPayload) => void;
  autoApply?: boolean;
  pending?: boolean;
  autoKey?: string;
  claimAuto?: (key: string) => boolean;
  wasAuto?: (key: string) => boolean;
}) {
  const { t } = useTranslation();
  const claimed = !!(autoKey && wasAuto?.(autoKey));
  const ui = chatAutoCardUi({
    claimed,
    autoApply: !!autoApply,
    pending,
  });
  const [state, setState] = useState<'idle' | 'applying' | 'done' | 'error'>(
    () => (ui === 'done' ? 'done' : ui === 'apply' ? 'applying' : 'idle'),
  );
  const [result, setResult] = useState<
    { sha?: string; error?: string; prevContent?: string | null; reverted?: boolean } | null
  >(null);
  const [reverting, setReverting] = useState(false);
  const autoStartedRef = useRef(false);

  // Detekuj jestli je content unified diff (--- +++) nebo plný nový obsah.
  // GitLab commit API potřebuje plný nový obsah — diff musí být applied klientsky.
  // Pro jednoduchost: pokud content začíná "--- " → diff (nezpracujeme, jen odkaz na commit tool),
  // jinak = plný obsah pro daný path.
  const looksLikeDiff = /^(---|\+\+\+|@@|diff --git)/m.test(content);

  const applyPatch = async () => {
    // Selhání schválené karty musí vidět i model, ne jen toast v rohu. Bez
    // `patch_error` followupu zůstane v historii dojem, že zápis proběhl, a
    // agent pokračuje ze stavu, který neexistuje (Filip 2026-09-09: „uvidím po
    // schválení chybu jen já, a nepošle se zpátky do chatu").
    if (!projectSlug || !path) {
      const error = t('codable.asNoPath');
      toast.error(error);
      setState('error');
      setResult({ error });
      onAction?.({ type: 'patch_error', block: content, meta: { path, error } });
      return;
    }
    if (looksLikeDiff) {
      const error = t('codable.asDiffUnsupported');
      toast.error(error);
      setState('error');
      setResult({ error });
      onAction?.({ type: 'patch_error', block: content, meta: { path, error } });
      return;
    }
    setState('applying');
    try {
      // Workspace-write model nemá per-edit commit → revert = zapsat starý
      // obsah zpět. Zachyť ho ZE ŽIVÉHO workspace před přepisem (chat_file_read
      // je disk-first). Když soubor neexistoval (nový), prevContent zůstane null
      // a Vrátit se nenabídne (nemáme delete tool k „un-create").
      let prevContent: string | null = null;
      try {
        const prev = await api.mcp<{ ok: boolean; content?: string }>(
          'codable.chat_file_read',
          { slug: projectSlug, path },
        );
        if (prev.ok && typeof prev.content === 'string') prevContent = prev.content;
      } catch { /* nový soubor / read selhal → revert nebude nabídnut */ }

      const r = await api.mcp<{ ok: boolean; error?: string }>(
        'codable.workspace_write',
        { slug: projectSlug, path, content },
      );
      if (!r.ok) {
        setState('error');
        const error = r.error ?? t('codable.asWriteFail');
        setResult({ error });
        toast.error(error);
        onAction?.({ type: 'patch_error', block: content, meta: { path, error } });
        return;
      }
      // Filip 2026-07-12: „agent má dělat úpravy na serveru a teprve následně
      // je přenášet do gitu." Zápis jde přímo do dev workspace — vite HMR
      // promítne změnu do preview okamžitě, bez commitu a bez rebuildu.
      // Git checkpoint = commit karta (codable.commit).
      toast.success(t('codable.asWrittenHmr'));
      setState('done');
      setResult({ prevContent });
      onAction?.({
        type: 'patch_apply',
        block: content,
        meta: { path, written: 'true' },
      });
    } catch (e) {
      setState('error');
      const msg = (e as Error).message;
      setResult({ error: msg });
      toast.error(msg);
      onAction?.({ type: 'patch_error', block: content, meta: { path, error: msg } });
    }
  };

  // Auto-apply (Auto-accept / Live): aplikuj sam, jen jednou (claim guard),
  // jen po dokonceni streamu. Claim fail / reload → badge done, ne Přijmout.
  // Unified diff se nedá aplikovat → hned patch_error followup (ať agent pošle full-file).
  useEffect(() => {
    if (ui === 'done') {
      if (state === 'idle') setState('done');
      return;
    }
    if (!autoApply || pending) return;
    if (state === 'done' || state === 'error') return;
    if (autoStartedRef.current) return;
    if (autoKey && wasAuto?.(autoKey)) {
      setState('done');
      return;
    }
    if (autoKey && claimAuto && !claimAuto(autoKey)) {
      setState('done');
      return;
    }
    autoStartedRef.current = true;
    if (looksLikeDiff) {
      setState('error');
      const error = t('codable.asDiffUnsupported');
      setResult({ error });
      onAction?.({
        type: 'patch_error',
        block: content,
        meta: { path, error },
      });
      return;
    }
    void applyPatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApply, pending, ui]);

  // „Vrátit" — Filip 2026-07-12: přímo v chatu vrátit úpravu, aby se to hned
  // provedlo. Model workspace-write: zapíšeme původní obsah zpět → vite HMR
  // reverzi okamžitě promítne do preview (bez rebuildu, bez git revertu).
  const revertPatch = async () => {
    if (reverting || !projectSlug || !path || result?.prevContent == null) return;
    setReverting(true);
    try {
      const r = await api.mcp<{ ok: boolean; error?: string }>(
        'codable.workspace_write',
        { slug: projectSlug, path, content: result.prevContent },
      );
      if (!r.ok) {
        toast.error(r.error ?? t('codable.asRevertFail'));
        return;
      }
      toast.success(t('codable.asRevertedHmr'));
      setResult((prev) => (prev ? { ...prev, reverted: true } : { reverted: true }));
      onAction?.({ type: 'patch_revert', block: '', meta: { path } });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setReverting(false);
    }
  };

  // Stavový chip: idle/applying amber → done emerald → error red
  const chipClass = state === 'done'
    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
    : state === 'error'
    ? 'bg-red-500/10 text-red-300 border-red-500/20'
    : 'bg-amber-500/10 text-amber-300 border-amber-500/20';

  return (
    // Sjednoceno s Plan/Question/Commit kartami (border+bg podle typu, header
    // řádek s ikonou+label) — dřív jediná neutrálně šedá karta mezi barevnými
    // (Filip 2026-07-18: "každá ta věc vypadá jinak, vypadá to slepovaně").
    // Amber = stejná barva jako stavový chip níže (idle/applying).
    <div className='border border-amber-500/30 bg-amber-500/5 rounded-xl p-3'>
      <div className='flex items-center gap-2 mb-2'>
        <Edit3 size={13} className='text-amber-400' />
        <span className='text-[11px] uppercase font-bold text-amber-400 tracking-wider'>
          {t('codable.asFileEdit', 'Úprava souboru')}
        </span>
      </div>
      <div className='flex items-center gap-2 mb-2 flex-wrap'>
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-lg border font-mono ${chipClass}`}
          title={path}
        >
          {state === 'applying'
            ? <Loader2 size={10} className='animate-spin shrink-0' />
            : state === 'done'
            ? <Check size={10} className='shrink-0' />
            : state === 'error'
            ? <XCircle size={10} className='shrink-0' />
            : <Edit3 size={10} className='shrink-0' />}
          <span className='truncate max-w-[260px]'>{path || t('codable.asFileFallback', 'soubor')}</span>
        </span>
        {looksLikeDiff && <span className='text-[10px] text-gray-500'>{t('codable.asDiffFormat', 'diff formát')}</span>}
      </div>
      <pre className='text-xs bg-gray-950 rounded-lg p-2 overflow-x-auto mb-3 max-h-[300px] overflow-y-auto'>
        <code>{content}</code>
      </pre>
      {state === 'done' && (
        <div className='flex items-center justify-between gap-2 mb-2'>
          <div className='text-xs text-emerald-400'>
            {result?.reverted
              ? t('codable.asRevertedHmrDetail', '↩ Úprava vrácena — preview se aktualizovalo (HMR).')
              : result
              ? t('codable.asWrittenHmrDetail', '✓ Zapsáno do workspace — preview se aktualizovalo (HMR). Git checkpoint přes commit kartu.')
              : t('codable.asWrittenAuto', '✓ Zapsáno automaticky (Live/Auto).')}
          </div>
          {!result?.reverted && result?.prevContent != null && (
            <Button
              size='sm'
              variant='ghost'
              icon={reverting ? <Loader2 size={13} className='animate-spin' /> : <Undo2 size={13} />}
              onClick={revertPatch}
              disabled={reverting}
              title={t('codable.asRevertHint')}
            >
              {reverting ? t('codable.asRevert') : t('codable.asRevertBtn')}
            </Button>
          )}
        </div>
      )}
      {state === 'error' && result?.error && (
        <div className='text-xs text-red-400 mb-2'>✗ {result.error}</div>
      )}
      {(state === 'idle' || state === 'error') && ui === 'buttons' && (
        <div className='flex gap-2'>
          <Button
            size='sm'
            variant='success'
            icon={<CheckCircle2 size={14} />}
            onClick={applyPatch}
            disabled={looksLikeDiff}
          >
            {t('codable.asUse')}
          </Button>
          <Button
            size='sm'
            variant='danger'
            icon={<XCircle size={14} />}
            onClick={() => onAction?.({ type: 'patch_reject', block: content, meta: { path } })}
          >
            {t('codable.asReject', 'Odmítnout')}
          </Button>
        </div>
      )}
      {state === 'error' && ui !== 'buttons' && (
        <div className='flex gap-2'>
          <Button
            size='sm'
            variant='success'
            icon={<CheckCircle2 size={14} />}
            onClick={applyPatch}
            disabled={looksLikeDiff}
          >
            {t('codable.asUse')}
          </Button>
        </div>
      )}
    </div>
  );
}

function CommitCard(
  {
    msg, body, projectSlug, onAction, autoCommit, pending, autoKey, claimAuto, wasAuto }: {
    msg: string;
    body: string;
    projectSlug?: string;
    onAction?: (p: ActionPayload) => void;
    autoCommit?: boolean;
    pending?: boolean;
    autoKey?: string;
    claimAuto?: (key: string) => boolean;
    wasAuto?: (key: string) => boolean;
  },
) {
  const { t } = useTranslation();
  const cleanMsg = msg.replace(/^["']|["']$/g, '').trim();
  const [editMsg, setEditMsg] = useState(cleanMsg);
  const claimed = !!(autoKey && wasAuto?.(autoKey));
  const ui = chatAutoCardUi({
    claimed,
    autoApply: !!autoCommit,
    pending,
  });
  // Klik = skutečný git commit+push workspace (codable.commit) — výsledek jde
  // agentovi v meta (ground truth místo „✅ hotovo" halucinací).
  const [state, setState] = useState<'idle' | 'committing' | 'done' | 'error'>(
    () => (ui === 'done' ? 'done' : ui === 'apply' ? 'committing' : 'idle'),
  );
  const autoStartedRef = useRef(false);
  const [result, setResult] = useState<
    { sha?: string; files?: number; error?: string; nothing?: boolean } | null
  >(null);

  const doCommit = async () => {
    if (!projectSlug) {
      const error = t('codable.asNoSlug');
      toast.error(error);
      setState('error');
      setResult({ error });
      return;
    }
    const message = editMsg.trim();
    if (!message) {
      const error = t('codable.asNoMessage');
      toast.error(error);
      setState('error');
      setResult({ error });
      return;
    }
    setState('committing');
    try {
      const r = await api.mcp<{
        ok: boolean;
        error?: string;
        short_sha?: string;
        files_changed?: number;
        nothing_to_commit?: boolean;
      }>(
        'codable.commit',
        { slug: projectSlug, message, ...(body.trim() ? { body: body.trim() } : {}) },
      );
      if (!r.ok) {
        setState('error');
        setResult({ error: r.error });
        toast.error(r.error ?? t('codable.asCommitFail'));
        onAction?.({
          type: 'commit_confirm',
          block: body,
          meta: { msg: message, ok: 'false', error: r.error ?? 'unknown' },
        });
        return;
      }
      if (r.nothing_to_commit) {
        setState('done');
        setResult({ nothing: true });
        onAction?.({
          type: 'commit_confirm',
          block: body,
          meta: { msg: message, ok: 'true', nothing: 'true' },
        });
        return;
      }
      setState('done');
      setResult({ sha: r.short_sha, files: r.files_changed });
      toast.success(t('codable.asCommitPushed', 'Commit {{sha}} pushnut na GitLab', { sha: r.short_sha }));
      onAction?.({
        type: 'commit_confirm',
        block: body,
        meta: {
          msg: message,
          ok: 'true',
          sha: r.short_sha ?? '',
          files: String(r.files_changed ?? 0),
        },
      });
    } catch (e) {
      setState('error');
      const error = (e as Error).message;
      setResult({ error });
      toast.error(error);
      onAction?.({
        type: 'commit_confirm',
        block: body,
        meta: { msg: message, ok: 'false', error },
      });
    }
  };

  // Live+Commit: auto push po dokončení streamu (stejný claim guard jako patch).
  // Claim fail / reload → badge done, ne tlačítka Přijmout/Odmítnout.
  useEffect(() => {
    if (ui === 'done') {
      if (state === 'idle') setState('done');
      return;
    }
    if (!autoCommit || pending) return;
    if (state === 'done' || state === 'error') return;
    if (autoStartedRef.current) return;
    if (autoKey && wasAuto?.(autoKey)) {
      setState('done');
      return;
    }
    if (autoKey && claimAuto && !claimAuto(autoKey)) {
      setState('done');
      return;
    }
    autoStartedRef.current = true;
    void doCommit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCommit, pending, ui]);

  return (
    <div className='border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-3'>
      <div className='flex items-center gap-2 mb-2'>
        <GitCommit size={13} className='text-emerald-400' />
        <span className='text-[11px] uppercase font-bold text-emerald-400 tracking-wider'>
          {t('codable.asCommitPush')}
        </span>
      </div>
      {(state === 'idle' || state === 'error') && ui === 'buttons'
        ? (
          <input
            value={editMsg}
            onChange={(e) => setEditMsg(e.target.value)}
            className='w-full mb-1 rounded-lg border border-gray-800 bg-gray-950 px-2 py-1.5 font-mono text-sm text-gray-200 outline-none focus:border-emerald-500/50'
            placeholder={t('codable.asMissingMessage', '(chybí commit message)')}
          />
        )
        : (
          <div className='font-mono text-sm text-gray-200 mb-1'>
            {editMsg.trim() || t('codable.asMissingMessage', '(chybí commit message)')}
          </div>
        )}
      {body.trim() && (
        <div className='text-xs text-gray-400 whitespace-pre-wrap mb-3'>{body.trim()}</div>
      )}
      {state === 'done' && (
        <div className='text-xs text-emerald-400 mb-2'>
          {result?.nothing
            ? t('codable.asNothingToCommit')
            : result?.sha
            ? (
              <>
                {t('codable.asCommitOkPrefix', '✓ Commit')}{' '}
                <span className='font-mono'>{result.sha}</span>{' '}
                {t('codable.asCommitFilesPushed', '({{count}} souborů) pushnut na GitLab.', { count: result?.files ?? 0 })}
              </>
            )
            : t('codable.asCommitAuto', '✓ Commit proveden automaticky (Live/Auto).')}
        </div>
      )}
      {state === 'error' && result?.error && (
        <div className='text-xs text-red-400 mb-2'>✗ {result.error}</div>
      )}
      {(state === 'idle' || state === 'error') && ui === 'buttons' && (
        <div className='flex gap-2'>
          <Button
            size='sm'
            variant='success'
            icon={<GitCommit size={14} />}
            onClick={doCommit}
          >
            {t('codable.asCommitPush')}
          </Button>
          <Button
            size='sm'
            variant='danger'
            icon={<XCircle size={14} />}
            onClick={() => onAction?.({ type: 'commit_reject', block: body, meta: { msg: editMsg.trim() } })}
          >
            {t('codable.asReject', 'Odmítnout')}
          </Button>
        </div>
      )}
      {state === 'error' && ui !== 'buttons' && (
        <div className='flex gap-2'>
          <Button
            size='sm'
            variant='success'
            icon={<GitCommit size={14} />}
            onClick={doCommit}
          >
            {t('codable.asCommitPush')}
          </Button>
        </div>
      )}
    </div>
  );
}

function QuestionCard({ content, onAction }: { content: string; onAction?: (p: ActionPayload) => void }) {
  // ask_user options (převzato ze superagent-tools.ts:1087): pokud content
  // obsahuje `options:` sekci s bullet list, renderujeme tlačítka místo
  // pouhého text hintu. Filip 2026-07-10: superagent to uměl — 4 HIGH ports.
  const { t } = useTranslation();
  const { question, options } = parseQuestionWithOptions(content);
  return (
    <div className='border border-purple-500/25 bg-gray-900/80 rounded-xl p-3'>
      <div className='flex items-center gap-2 mb-2'>
        <HelpCircle size={13} className='text-purple-400 shrink-0' />
        <span className='text-[11px] uppercase font-bold text-purple-400 tracking-wider'>
          {t('codable.chatQuestion')}
        </span>
      </div>
      <div className={`${PROSE_CLASSES} mb-2`}>
        <ReactMarkdown>{question}</ReactMarkdown>
      </div>
      {options.length > 0
        ? (
          <div className='flex flex-wrap gap-2 mt-2'>
            {options.map((opt, i) => (
              <Button
                key={i}
                size='sm'
                variant='secondary'
                onClick={() => onAction?.({ type: 'question_reply', block: opt, meta: { text: opt } })}
              >
                {opt}
              </Button>
            ))}
            <span className='text-[11px] text-gray-500 self-center ml-1'>
              {t('codable.asOrTypeReply', 'nebo napiš vlastní odpověď dole')}
            </span>
          </div>
        )
        : <div className='text-xs text-gray-500'>{t('codable.asWaitInChat', 'Odpověz v chatu — asistent bude čekat.')}</div>}
    </div>
  );
}

/**
 * Parsuje question fence s volitelným options seznamem:
 *
 *   Chceš přidat dark mode?
 *   options:
 *     - Ano, s toggle v headeru
 *     - Ano, follow-system default
 *     - Ne
 *
 * Split na první `options:` řádek; predchozi text = otázka, následující
 * bullety = tlačítka.
 */
function parseQuestionWithOptions(content: string): { question: string; options: string[] } {
  const optionsRe = /^\s*options\s*:\s*$/im;
  const m = content.match(optionsRe);
  if (!m || m.index === undefined) return { question: content.trim(), options: [] };
  const question = content.slice(0, m.index).trim();
  const rest = content.slice(m.index + m[0].length).trim();
  const options: string[] = [];
  for (const line of rest.split('\n')) {
    const bulletM = line.match(/^\s*[-*]\s+(.+)$/);
    if (bulletM?.[1]) {
      options.push(bulletM[1].trim());
    }
  }
  return { question, options };
}
